/**
 * useAccountArchive Hook
 * 
 * Manages archive and unarchive operations for accounts.
 * Handles validation, confirmation dialogs, and Firestore updates.
 */

import { useState } from "react";
import { auth } from "../../../firebase";
import { doc, updateDoc, serverTimestamp, deleteField } from "firebase/firestore";
import { db } from "../../../firebase";
import { Account } from "../../../types";
import { isDemoMode } from "../../../utils/demoMode";
import { DemoModeMessage } from "../../../utils/demoModeMessage";

interface UseAccountArchiveParams {
  workspaceId: string | undefined;
  setSnackbarElement: (element: React.ReactElement | null) => void;
  setSnackbarOpen: (open: boolean) => void;
}

interface UseAccountArchiveReturn {
  // Archive state
  archiveConfirmOpen: boolean;
  setArchiveConfirmOpen: React.Dispatch<React.SetStateAction<boolean>>;
  accountToArchive: Account | null;
  setAccountToArchive: React.Dispatch<React.SetStateAction<Account | null>>;
  
  // Unarchive state
  unarchiveConfirmOpen: boolean;
  setUnarchiveConfirmOpen: React.Dispatch<React.SetStateAction<boolean>>;
  accountToUnarchive: Account | null;
  setAccountToUnarchive: React.Dispatch<React.SetStateAction<Account | null>>;
  
  // Handlers
  handleArchiveClick: (account: Account) => void;
  handleArchiveConfirm: () => Promise<void>;
  handleUnarchiveClick: (account: Account) => void;
  handleUnarchiveConfirm: () => Promise<void>;
}

/**
 * Hook to manage account archive/unarchive operations
 */
export function useAccountArchive({
  workspaceId,
  setSnackbarElement,
  setSnackbarOpen,
}: UseAccountArchiveParams): UseAccountArchiveReturn {
  // Archive state
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState<boolean>(false);
  const [accountToArchive, setAccountToArchive] = useState<Account | null>(null);
  
  // Unarchive state
  const [unarchiveConfirmOpen, setUnarchiveConfirmOpen] = useState<boolean>(false);
  const [accountToUnarchive, setAccountToUnarchive] = useState<Account | null>(null);
  
  // Handle archive click - opens confirmation dialog
  const handleArchiveClick = (account: Account): void => {
    setAccountToArchive(account);
    setArchiveConfirmOpen(true);
  };
  
  // Confirm and execute account archiving
  const handleArchiveConfirm = async (): Promise<void> => {
    // In demo mode, accounts cannot be archived
    if (isDemoMode()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Accounts cannot be archived. Please log in to archive accounts." />
      );
      setSnackbarOpen(true);
      setArchiveConfirmOpen(false);
      setAccountToArchive(null);
      return;
    }
    
    if (!accountToArchive || !workspaceId || !auth.currentUser) {
      setArchiveConfirmOpen(false);
      setAccountToArchive(null);
      return;
    }
    
    // Check if account amount is 0
    const accountAmount = parseFloat(String(accountToArchive.amount || 0));
    if (accountAmount !== 0) {
      alert("Cannot archive account with non-zero balance. Please transfer or adjust the balance to 0 first.");
      setArchiveConfirmOpen(false);
      setAccountToArchive(null);
      return;
    }
    
    try {
      const accountRef = doc(db, "workspaces", workspaceId, "accounts", accountToArchive.id);
      await updateDoc(accountRef, {
        isArchived: true,
        archivedAt: serverTimestamp(),
      });
      
      setArchiveConfirmOpen(false);
      setAccountToArchive(null);
    } catch (error) {
      console.error("Error archiving account:", error);
      alert("Failed to archive account");
      setArchiveConfirmOpen(false);
      setAccountToArchive(null);
    }
  };
  
  // Handle unarchive click - opens confirmation dialog
  const handleUnarchiveClick = (account: Account): void => {
    setAccountToUnarchive(account);
    setUnarchiveConfirmOpen(true);
  };
  
  // Confirm and execute account unarchiving
  const handleUnarchiveConfirm = async (): Promise<void> => {
    // In demo mode, accounts cannot be unarchived
    if (isDemoMode()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Accounts cannot be unarchived. Please log in to unarchive accounts." />
      );
      setSnackbarOpen(true);
      setUnarchiveConfirmOpen(false);
      setAccountToUnarchive(null);
      return;
    }
    
    if (!accountToUnarchive || !workspaceId || !auth.currentUser) {
      setUnarchiveConfirmOpen(false);
      setAccountToUnarchive(null);
      return;
    }
    
    try {
      const accountRef = doc(db, "workspaces", workspaceId, "accounts", accountToUnarchive.id);
      await updateDoc(accountRef, {
        isArchived: false,
        archivedAt: deleteField(),
      });
      
      setUnarchiveConfirmOpen(false);
      setAccountToUnarchive(null);
    } catch (error) {
      console.error("Error unarchiving account:", error);
      alert("Failed to unarchive account");
      setUnarchiveConfirmOpen(false);
      setAccountToUnarchive(null);
    }
  };
  
  return {
    // Archive state
    archiveConfirmOpen,
    setArchiveConfirmOpen,
    accountToArchive,
    setAccountToArchive,
    
    // Unarchive state
    unarchiveConfirmOpen,
    setUnarchiveConfirmOpen,
    accountToUnarchive,
    setAccountToUnarchive,
    
    // Handlers
    handleArchiveClick,
    handleArchiveConfirm,
    handleUnarchiveClick,
    handleUnarchiveConfirm,
  };
}
